import  { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Button, Card, Col, Input, Label, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchByGaurd } from "../../store/dashboard/actions";
import {  getEndOfDay, getTimestamp } from "../../helpers/formatDate";
import { CommonDatePicker } from "../../components/Common/Datepicker/CommonDatePicker";
import { CommonMyDatePicker } from "../../components/Common/Datepicker/CommonMyDatePicker";

const GuardWiseTicketsTable = () => {
  const [guardName, setGuardName] = useState("");
  const [debouncedGuardName, setDebouncedGuardName] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const dispatch = useDispatch();
  const { guardData } = useSelector((state) => state.dashboardReducer);

  const handleFromDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(getEndOfDay(date));
  };
  const today = new Date();

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setFromDate(getTimestamp(thirtyDaysAgo));

    setToDate(getTimestamp(today));
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedGuardName(guardName);
    }, 500);

    return () => clearTimeout(handler);
  }, [guardName]);
  // Fetch guards when debounced value updates
  useEffect(() => {
    dispatch(fetchByGaurd(debouncedGuardName, fromDate, toDate));
  }, [debouncedGuardName, dispatch,fromDate, toDate]);

  const transformedData =
    guardData?.guards?.map((guard) => {
      const fullName = `${guard?.firstName?.trim()} ${guard?.lastName?.trim()}`;
      const trimmedFullName = fullName.replace(/\s+/g, " ").trim(); // Normalize multiple spaces

      // Normalize keys in guardWiseCounts for safer matching
      const normalizedCounts = Object.entries(
        guardData?.counts?.guardWiseCounts || {}
      ).reduce((acc, [key, value]) => {
        const normalizedKey = key.replace(/\s+/g, " ").trim();
        acc[normalizedKey] = value;
        return acc;
      }, {});

      const ticketCounts = normalizedCounts[trimmedFullName] || {};

      return {
        guardId: guard.guardId,
        name: trimmedFullName,
        assigned: ticketCounts.Assigned || 0,
        acknowledged: ticketCounts.Acknowledged || 0,
        working: ticketCounts.Working || 0,
        resolved: ticketCounts.Resolved || 0,
        closed: ticketCounts.Closed || 0,
        reopen: ticketCounts.Reopen || 0,
      };
    }) || [];

  // Define all table columns
  const allColumns = [
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <span style={{ textTransform: "capitalize" }}>{`${row.name}`}</span>
      ),
      sortable: true,
      id: "name",
      alwaysVisible: true,
    },
    {
      name: "Assigned",
      selector: (row) => row.assigned,
      sortable: true,
      id: "assigned",
    },
    {
      name: "Acknowledged",
      selector: (row) => row.acknowledged,
      sortable: true,
      id: "acknowledged",
    },
    {
      name: "Working",
      selector: (row) => row.working,
      sortable: true,
      id: "working",
    },
    {
      name: "Resolved",
      selector: (row) => row.resolved,
      sortable: true,
      id: "resolved",
    },
    {
      name: "Closed",
      selector: (row) => row.closed,
      sortable: true,
      id: "closed",
    },
    {
      name: "Reopen",
      selector: (row) => row.reopen,
      sortable: true,
      id: "reopen",
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(allColumns);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  // Toggle column visibility
  const toggleColumnVisibility = (columnId) => {
    setVisibleColumns((prev) =>
      prev.some((col) => col.id === columnId)
        ? prev.filter((col) => col.id !== columnId)
        : [...prev, allColumns.find((col) => col.id === columnId)]
    );
  };

  const exportData = (format) => {
    const selectedColumns = allColumns.filter((col) =>
      visibleColumns.some((vc) => vc.id === col.id)
    );

    const data = transformedData.map((row) =>
      selectedColumns.reduce((acc, col) => {
        acc[col.name] = col.selector(row);
        return acc;
      }, {})
    );

    if (format === "excel") {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Guards");
      XLSX.writeFile(workbook, "GuardWiseTickets.xlsx");
    } else if (format === "pdf") {
      const doc = new jsPDF();
      doc.text("Guard Wise Tickets", 14, 15);
      doc.autoTable({
        head: [selectedColumns.map((col) => col.name)],
        body: data.map((row) => selectedColumns.map((col) => row[col.name])),
        startY: 20,
      });
      doc.save("GuardWiseTickets.pdf");
    }
  };

  return (
    <Card className="mb-4 p-4">
      <div className="table-container mb-5">
        <Row className="mb-3">
          <Col md={2}>
            <CommonDatePicker
              placeholderText="Start Date"
                                label=" Start Date"

              value={fromDate}
              onChange={handleFromDateChange}
              disabled={false}
              maxDate={today}
            />
          </Col>
          <Col md={2}>
            <CommonMyDatePicker
              placeholderText="End Date"
                                label=" End Date"

              value={toDate}
              onChange={handleToDateChange}
              maxDate={today}
              minDate={fromDate}
            />
          </Col>
          <Col lg={2}>
          <Label for="dateFilter">Search</Label>
            <Input
              type="text"
              placeholder="Search by name"
              value={guardName}
              onChange={(e) => setGuardName(e.target.value)}
            />
          </Col>

          <Col lg={6} className="text-end">
            <Button
              color="primary"
              onClick={() => setDropdownVisible(!dropdownVisible)}
              className="me-2"
            >
              Manage Columns
            </Button>
            {dropdownVisible && (
              <div
                style={{
                  position: "absolute",
                  right: "5%",
                  background: "white",
                  border: "1px solid #ccc",
                  padding: "10px",
                  zIndex: "99",
                  boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                }}
              >
                {allColumns
                  .filter((col) => !col.alwaysVisible)
                  .map((column) => (
                    <div key={column.id}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.some(
                          (col) => col.id === column.id
                        )}
                        onChange={() => toggleColumnVisibility(column.id)}
                      />
                      <label style={{ marginLeft: "8px" }}>{column.name}</label>
                    </div>
                  ))}
              </div>
            )}
            <Button
              color="primary"
              onClick={() => exportData("excel")}
              className="me-2"
            >
              Export to Excel
            </Button>
            <Button color="primary" onClick={() => exportData("pdf")}>
              Export to PDF
            </Button>
          </Col>
        </Row>

        <DataTable columns={visibleColumns} data={transformedData} pagination />
      </div>
    </Card>
  );
};

export default GuardWiseTicketsTable;
